import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLanguage, LANGUAGE_OPTIONS, languageName } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

// Плашка с настройками интерфейса: тема и язык. Тема живет здесь, а не отдельной
// кнопкой, чтобы попадать во все места, где плашка уже показывается, — включая
// мобильное меню, где для нее нет своего угла
const LanguageSwitcher = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const optionRefs = useRef([]);

  const themeTitle = theme === 'dark'
    ? t('common.lightTheme')
    : t('common.darkTheme');

  // Плашка на лендинге и страницах входа прижата к низу экрана — там список
  // раскрывается вверх, в шапке мобильного меню — вниз. Меряем до отрисовки,
  // чтобы список не мигнул не в ту сторону
  useLayoutEffect(() => {
    if (!open) return;

    const rect = rootRef.current.getBoundingClientRect();
    setDropUp(rect.bottom + listRef.current.offsetHeight + 12 > window.innerHeight);

    const selected = LANGUAGE_OPTIONS.findIndex((option) => option.code === language);
    optionRefs.current[Math.max(selected, 0)]?.focus();
  }, [open, language]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('touchstart', closeOnOutside);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('touchstart', closeOnOutside);
    };
  }, [open]);

  const choose = (code) => {
    setLanguage(code);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handleListKeyDown = (event) => {
    const count = LANGUAGE_OPTIONS.length;
    const index = optionRefs.current.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      optionRefs.current[(index + step + count) % count]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (index >= 0) choose(LANGUAGE_OPTIONS[index].code);
    } else if (event.key === 'Escape') {
      setOpen(false);
      buttonRef.current?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="language-switcher">
      <button
        className="theme-btn"
        onClick={toggleTheme}
        title={themeTitle}
        aria-label={themeTitle}
      >
        {theme === 'dark' ? '☀' : '☾'}
      </button>

      <span className="switcher-divider" aria-hidden="true" />

      {/* Языков будет больше, поэтому это список, а не ряд кнопок. Список свой,
          а не нативный select: он оформлен так же, как сама плашка */}
      <div
        ref={rootRef}
        className={`lang-select ${open ? 'open' : ''} ${dropUp ? 'drop-up' : ''}`}
      >
        <button
          ref={buttonRef}
          type="button"
          className="lang-select-button"
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${t('profile.language')}: ${languageName(language)}`}
        >
          <span className="lang-select-value">{languageName(language)}</span>
          <span className="lang-select-arrow" aria-hidden="true">▾</span>
        </button>

        {open && (
          <ul
            ref={listRef}
            className="lang-select-list"
            role="listbox"
            aria-label={t('profile.language')}
            onKeyDown={handleListKeyDown}
          >
            {LANGUAGE_OPTIONS.map(({ code, name }, index) => (
              <li
                key={code}
                ref={(element) => { optionRefs.current[index] = element; }}
                className="lang-select-option"
                role="option"
                aria-selected={code === language}
                tabIndex={-1}
                onClick={() => choose(code)}
              >
                {name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
